import { ReactNode, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useContractorProfile } from '@/hooks/useContractorProfile';
import { Loader2, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface PortalRouteGuardProps {
  children: ReactNode;
}

export function PortalRouteGuard({ children }: PortalRouteGuardProps) {
  const { user, loading: authLoading } = useAuth();
  const { profile, isLoading: profileLoading } = useContractorProfile();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/contractor-portal/auth');
    }
  }, [user, authLoading, navigate]);

  if (authLoading || profileLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/contractor-portal/auth" replace />;
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Access denied. You do not have a contractor portal account.
            Please contact your project manager for assistance.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!profile.is_active) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Your account has been deactivated. Please contact your project manager
            to reactivate your access.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return <>{children}</>;
}
